import type { EphemerisResult, SearchResult } from "../../components/types.js"
import {
  commandFromSupportId,
  computeStopTimeUtc,
  getHorizonsStepSize,
  parseHorizonsResult,
} from "../horizons.js"

export type HorizonsEphemerisRequest = {
  target: string
  dateUtc: string // YYYY-MM-DD
  startTimeUtc: string // HH:MM
  interval: string // 5m|15m|1h|1d
  site:
    | { type: "mpc"; siteCode: string }
    | { type: "coords"; lat: number; lon: number; elevKm: number }
}

export async function searchHorizonsObject(
  query: string,
  baseUrl: string = window.location.origin
): Promise<SearchResult> {
  const url = new URL("/api/horizons_support.api", baseUrl)
  url.searchParams.set("sstr", query)
  url.searchParams.set("time-span", "1")
  url.searchParams.set("www", "1")

  const response = await fetch(url)

  // HTTP 300 means multiple matches (valid response), not an error
  if (!response.ok && response.status !== 300) {
    throw new Error(`Horizons support search failed: ${response.status}`)
  }

  const json = await response.json()

  if (json.count === 1 && json.data) {
    return {
      type: "unique",
      recordNumber: commandFromSupportId(json.data.kind, json.data.id),
      name: json.data.name,
      kind: json.data.kind,
    }
  }

  if (
    (json.code === "300" || response.status === 300) &&
    json.list &&
    json.list.length > 0
  ) {
    const matches = json.list.map((item: any) => ({
      recordNumber: commandFromSupportId(item.kind, item.id),
      name: item.name,
      kind: item.kind,
    }))
    return { type: "multiple", matches }
  }

  return { type: "not_found" }
}

export async function fetchHorizonsEphemeris(
  req: HorizonsEphemerisRequest
): Promise<EphemerisResult> {
  // #region agent log
  fetch("http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      location: "horizonsClient.ts:60",
      message: "fetchHorizonsEphemeris called",
      data: {
        target: req.target,
        dateUtc: req.dateUtc,
        startTimeUtc: req.startTimeUtc,
        interval: req.interval,
      },
      timestamp: Date.now(),
      sessionId: "debug-session",
      runId: "fix-verification",
      hypothesisId: "D",
    }),
  }).catch(() => {})
  // #endregion
  const start = `${req.dateUtc} ${req.startTimeUtc}:00`
  const stop = computeStopTimeUtc(req.dateUtc, req.startTimeUtc, req.interval)
  const stepSize = getHorizonsStepSize(req.interval)

  // Qty 1: Astrometric RA/DEC
  // Qty 4: Apparent AZ/EL
  // Qty 25: T-O-M separation, MN_Illu%, and twilight/moon markers
  const params = new URLSearchParams({
    format: "json",
    COMMAND: `'${req.target}'`,
    EPHEM_TYPE: "OBSERVER",
    START_TIME: `'${start} UT'`,
    STOP_TIME: `'${stop}'`,
    STEP_SIZE: `'${stepSize}'`,
    CSV_FORMAT: "'YES'",
    QUANTITIES: "'1,4,25'",
    ANG_FORMAT: "'HMS'",
  })

  if (req.site.type === "coords") {
    params.set("CENTER", "'coord@399'")
    params.set(
      "SITE_COORD",
      `'${req.site.lon},${req.site.lat},${req.site.elevKm}'`
    )
  } else {
    params.set("CENTER", `'${req.site.siteCode}'`)
  }

  const response = await fetch("/api/horizons.api", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  })

  if (!response.ok) {
    throw new Error(`HORIZONS responded with ${response.status}`)
  }

  const json = await response.json()
  if (json?.error) {
    throw new Error(`HORIZONS API error: ${json.error}`)
  }
  if (!json?.result) {
    throw new Error("Unexpected HORIZONS response payload.")
  }

  return parseHorizonsResult(String(json.result))
}
